import React, { FC, useState } from 'react';

import { useMutation } from '@apollo/react-hooks';
import { Snackbar, TextField } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';

import Autocomplete from '../../../../components/Autocomplete/Autocomplete';
import { CustomButton } from '../../../../components/UI/CustomButton';
import { UPDATE_LEARNER_INFO } from '../../../../graphql/user';
import { LearnerInfo, Mutation, UpdateLearnerInfoArgs } from '../../../../types';
import { SelectOption } from '../../pages/const';
import useStyles from './styles';

const nameRegExp = new RegExp(/^[a-zA-Z '-]+$/, 'g');

interface AccountDetailsFormProps {
  userInfo?: LearnerInfo;
  categoriesList: SelectOption[];
  setFormEnabled: Function;
}

const AccountDetailsForm: FC<AccountDetailsFormProps> = ({
  userInfo,
  categoriesList,
  setFormEnabled,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [firstName, setFirstName] = useState(userInfo?.first_name || '');
  const [lastName, setLastName] = useState(userInfo?.last_name || '');
  const [jobTitle, setJobTitle] = useState(userInfo?.job || '');
  const [areasOfInterest, setAreasOfInterest] = useState<SelectOption[]>(
    userInfo?.interests
      ? userInfo.interests
          .filter((item) => item?.id && item?.title)
          .map((item) => ({
            value: item?.id as string,
            label: item?.title as string,
          }))
      : [],
  );

  const [firstNameErr, setFirstNameErr] = useState('');
  const [lastNameErr, setLastNameErr] = useState('');

  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);

  const [updateLearnerInfo] = useMutation<
    Pick<Mutation, 'updateLearnerInfo'>,
    UpdateLearnerInfoArgs
  >(UPDATE_LEARNER_INFO, {
    onCompleted: () => {
      setTouched(false);
      setLoading(false);
      setOpenAlert(true);
      setFormEnabled(false);
    },
  });

  const validateName = (val: string, setError: React.Dispatch<React.SetStateAction<string>>) => {
    if (!val.trim()) {
      setError(t('error.fieldRequired'));
      return false;
    } else if (!val.trim().match(nameRegExp)) {
      setError(t('error.lettersSymbolsAndSpace'));
      return false;
    }

    setError('');
    return true;
  };

  const hideAlert = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenAlert(false);
  };

  const handleSubmitForm = () => {
    if (validateName(firstName, setFirstNameErr) && validateName(lastName, setLastNameErr)) {
      setLoading(true);
      updateLearnerInfo({
        variables: {
          input: {
            first_name: firstName,
            last_name: lastName,
            job: jobTitle,
            interests: areasOfInterest.map((item) => Number(item.value)),
          },
        },
      });
    } else {
      setTouched(true);
    }
  };

  return (
    <>
      <form
        className={classes.wrapper}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmitForm();
        }}
      >
        <div className={classes.col}>
          <TextField
            inputProps={{ 'data-testid': 'firstName' }}
            className={classes.input}
            variant="outlined"
            name="firstName"
            label={t('page.account.firstName')}
            value={firstName}
            onChange={(event) => {
              setFirstName(event.target.value);
              if (touched) {
                validateName(event.target.value, setFirstNameErr);
              }
            }}
            error={touched && !!firstNameErr}
            helperText={touched && firstNameErr}
          />
          <TextField
            inputProps={{ 'data-testid': 'lastName' }}
            className={classes.input}
            variant="outlined"
            name="lastName"
            label={t('page.account.lastName')}
            value={lastName}
            onChange={(event) => {
              setLastName(event.target.value);
              if (touched) {
                validateName(event.target.value, setLastNameErr);
              }
            }}
            error={touched && !!lastNameErr}
            helperText={touched && lastNameErr}
          />
        </div>
        <div className={classes.col}>
          <TextField
            inputProps={{ 'data-testid': 'jobTitle' }}
            className={classes.input}
            variant="outlined"
            name="jobTitle"
            label={t('page.account.jobTitle')}
            value={jobTitle}
            onChange={(event) => {
              setJobTitle(event.target.value);
            }}
          />
          <Autocomplete
            className={classes.input}
            options={categoriesList}
            multiple
            getOptionLabel={(option) => option?.label || ''}
            value={areasOfInterest}
            onChange={(event, value) => {
              if (value) {
                setAreasOfInterest(value);
              } else {
                setAreasOfInterest([]);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                name="areasOfInterest"
                label={t('page.account.areasOfInterest')}
                variant="outlined"
              />
            )}
            data-testid="interestsSelect"
          />
        </div>
        <CustomButton
          data-testid="saveButton"
          id="save-btn"
          disabled={loading}
          type="submit"
          btnClass={classes.btn}
        >
          {t('button.save')}
        </CustomButton>
      </form>
      <Snackbar open={openAlert} autoHideDuration={3000} onClose={hideAlert}>
        <MuiAlert elevation={6} variant="filled" onClose={hideAlert} severity="success">
          {t('alert.saved')}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default AccountDetailsForm;
